import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Check, X, RefreshCw } from 'lucide-react';
import apiService from '../../services/apiService';
import { logger } from '../../utils/logger';
import { useLanguage } from '../../i18n';
import { userErrorMessage } from '../../utils/userError';
import useCompactWidth from '../../hooks/useCompactWidth';
import {
    CARD_ACTION_ROW, CARD_BTN_FIT, CARD_BTN_LABEL, CARD_ICON, CARD_SUMMARY_TEXT, CARD_COMPACT_WIDTH,
} from './cardLayout';

// ---------------------------------------------------------------------------
// ResolutionCard — the single, config-driven engine behind every chat
// confirmation card (H-3.2). It replaces ~14 near-identical
// status/busy/error/removedIds/persist state machines that used to live inline
// in Message.jsx.
//
// A card config (see ./cardConfigs.jsx) declares WHAT varies:
//   {
// cardKey,                 // metadata key + BE card_key (e.g. 'document_delete_request')
// testId,                  // data-testid on the outer container
// accent,                  // 'rose' | 'indigo' — border/title/action button hue
// icon,                    // lucide icon component for the header + summaries
// isValid(payload),        // guard — return false to render nothing
// getItems(payload),       // [] of row items (batch cards); [] for single-detail cards
// itemKey(item),           // stable React key / removal id
// minVisibleToRemove,      // 1 => X shown while >1 remain; 0 => X always shown
// cancelOnLastRemove,      // when true, removing the last item cancels instead of leaving 0
// title({t, count, payload}),           // header text
// renderDetail?({payload, t}),          // single-detail body (book/calendar/column)
// renderRow?({item, ctx}),              // per-row renderer for batch cards
// action: {
// status,                             // terminal status on success (e.g. 'deleted')
// label({t, count}),                  // dynamic button label
// busyLabel({t}),                     // label while executing
// icon,                               // button icon
// run({items, payload, cardProps, options}), // executor: per-item loop OR single call.
//                                           // returns {count, failuresText?} — engine sets
//                                           // error from failuresText and does NOT resolve.
//     },
// cancel: { status },                   // terminal status on cancel (e.g. 'cancelled')
// renderDone({payload, count, t}),      // summary after success
// renderCancelled({payload, items, t}), // summary after cancel
// noteAfterButtons?({t}),               // small helper line right of the buttons
// customBody?,                          // full-custom body (contact card) — overrides row/detail
// footerNote?({t}),                     // small line under the buttons
// initialOptions?(payload),             // per-card choices handed to action.run as `options`
// renderOptions?({active, payload, options, setOption, busy, t}), // UI for those choices
//                                       // (e.g. #498 follow-up: keep / delete the attached file)
//   }
//
// Cross-cutting fixes baked in (H-3.2):
//   - NEVER mutates message.metadata in place. Resolution state is component
// state seeded from props (payload.resolution) and persisted via the endpoint.
//   - persist() failures: logger.warn(conv/msg id) + ONE retry + a subtle
// non-blocking "couldn't save decision" hint on the card.
//   - user-facing strings route through i18n t().
// ---------------------------------------------------------------------------

// #517: the server keeps a resolved card FINAL — a different decision on an
// already-answered card is refused with 409 `card_already_resolved` carrying the
// resolution it keeps. That is not a failure to retry: the card adopts it.
const alreadyResolved = (err) => (err?.status === 409 && err?.body?.error?.type === 'card_already_resolved'
    ? (err.body.resolution || null) : null);

// Persist a card's resolution to the generic BE endpoint (H-3.1) with ONE retry.
// Resolves {ok, resolution?}: ok=false after both attempts fail; `resolution` is
// the server's kept decision when the card was already answered (#517). Never throws.
async function persistResolutionResult({ conversationId, messageId, cardKey, status, count, actionId, entryId }) {
    if (!conversationId || !messageId) return { ok: true }; // nothing to persist against (optimistic-only)
    const body = { card_key: cardKey, status };
    if (count != null) body.count = count;
    if (actionId) body.action_id = actionId;
    // #515: one of several cards of the same kind on a message (contact cards) —
    // resolve THIS entry of the array slot, not the scalar.
    if (entryId) body.entry_id = entryId;
    const attempt = () => apiService.resolveCardState(conversationId, messageId, body);
    try {
        await attempt();
        return { ok: true };
    } catch (err) {
        const kept = alreadyResolved(err);
        if (kept) return { ok: true, resolution: kept };
        logger.warn('RESOLUTION_CARD', `persist failed for ${cardKey} conv=${conversationId} msg=${messageId}: ${err?.message || err} — retrying once`);
        try {
            await attempt();
            return { ok: true };
        } catch (err2) {
            const kept2 = alreadyResolved(err2);
            if (kept2) return { ok: true, resolution: kept2 };
            logger.warn('RESOLUTION_CARD', `persist retry failed for ${cardKey} conv=${conversationId} msg=${messageId}: ${err2?.message || err2}`);
            return { ok: false };
        }
    }
}

// Boolean form kept for callers that only need success (rule card's Edit).
async function persistResolution(args) {
    return (await persistResolutionResult(args)).ok;
}

const ACCENTS = {
    rose: {
        containerBusy: 'border-rose-300 dark:border-rose-700 bg-rose-50/40 dark:bg-rose-900/10',
        title: 'text-rose-800 dark:text-rose-300',
        actionBtn: 'text-white bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300',
    },
    indigo: {
        containerBusy: 'border-[rgba(74,144,240,0.35)] bg-[rgba(74,144,240,0.07)]',
        title: 'text-pai-blue',
        actionBtn: 'bg-white text-pai-void hover:bg-white/90 disabled:bg-white/30',
    },
};

// #517: `items-start` + the sentence in ONE min-w-0 span (see renderSummary) —
// the summary reads as a sentence that wraps, never as columns.
const SUMMARY_DONE = 'mt-2 border border-pai-subtle bg-white/[0.025] rounded-lg p-3 text-xs text-pai-secondary flex items-start gap-2 min-w-0';
const SUMMARY_CANCELLED = 'mt-2 border border-pai-subtle bg-white/[0.025] rounded-lg p-3 text-xs text-pai-meta flex items-start gap-2 min-w-0';

const ResolutionCard = ({ config, payload, conversationId, messageId, entryId = null, cardProps = {} }) => {
    const { t } = useLanguage();

    const items = useMemo(
        () => (config.getItems ? config.getItems(payload) : []),
        [config, payload],
    );

    // Seed status from the PERSISTED resolution so a resolved card stays resolved
    // across reload / conversation switch (Deletion Card State Persistence #58 &
    // siblings). Component state — we NEVER write back into payload.resolution.
    const [status, setStatus] = useState(payload?.resolution?.status || 'pending');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [saveHint, setSaveHint] = useState(false); // subtle "couldn't save" hint
    const [removedIds, setRemovedIds] = useState(() => new Set());
    // Count from the persisted resolution wins after reload; otherwise the count the
    // executor reported this session.
    const [resolvedCount, setResolvedCount] = useState(payload?.resolution?.count ?? null);
    // Extra metadata a custom body wants to carry into its done summary this session
    // (e.g. the contact card's possibly-edited name).
    const [doneMeta, setDoneMeta] = useState(null);
    // Per-card choices the user makes before acting (#498 follow-up: delete the
    // attached file too?). Session-only; the executor receives them as `options`.
    const [options, setOptions] = useState(() => (config.initialOptions ? config.initialOptions(payload) : {}));
    const setOption = useCallback((key, value) => setOptions((prev) => ({ ...prev, [key]: value })), []);
    // #517: a narrow card (split view / narrow chat) collapses Edit to its icon.
    const [cardRef, compact] = useCompactWidth(CARD_COMPACT_WIDTH);

    // #507: adopt a persisted resolution that arrives AFTER mount (the conversation
    // re-fetch swaps in the resolved payload for a still-mounted card) — a resolved
    // card never stays live just because it mounted from an older payload.
    const persistedStatus = payload?.resolution?.status;
    const persistedCount = payload?.resolution?.count;
    useEffect(() => {
        if (!persistedStatus || busy) return;
        setStatus((cur) => (cur === 'pending' ? persistedStatus : cur));
        if (persistedCount != null) setResolvedCount((cur) => cur ?? persistedCount);
    }, [persistedStatus, persistedCount, busy]);

    const active = useMemo(
        () => items.filter((it) => !removedIds.has(config.itemKey(it))),
        [items, removedIds, config],
    );

    const actionId = payload?.action_id || null;

    // #517: adopt the decision the server kept for an already-answered card —
    // the card shows that final state instead of the one just clicked.
    const adoptResolution = useCallback((resolution) => {
        if (!resolution?.status) return;
        setStatus(resolution.status);
        if (resolution.count != null) setResolvedCount(resolution.count);
    }, []);

    const persist = useCallback(async (nextStatus, count) => {
        const res = await persistResolutionResult({
            conversationId, messageId, cardKey: config.cardKey,
            status: nextStatus, count, actionId, entryId,
        });
        if (!res.ok) setSaveHint(true);
        else if (res.resolution && res.resolution.status !== nextStatus) adoptResolution(res.resolution);
    }, [conversationId, messageId, config.cardKey, actionId, entryId, adoptResolution]);

    const doCancel = useCallback(() => {
        const cancelStatus = config.cancel?.status || 'cancelled';
        setStatus(cancelStatus);
        persist(cancelStatus);
    }, [config.cancel, persist]);

    const removeFromQueue = useCallback((id) => {
        setRemovedIds((prev) => {
            const next = new Set(prev);
            next.add(id);
            // Cancel-on-last-remove cards (ExpenseDelete #115, TicketAttach, DocRename)
            // fold to cancelled when the user drops the final row.
            if (config.cancelOnLastRemove &&
                items.filter((it) => !next.has(config.itemKey(it))).length === 0) {
                doCancel();
            }
            return next;
        });
    }, [config, items, doCancel]);

    const handleAction = useCallback(async () => {
        // Batch cards: nothing left to act on. Single-detail cards have no items.
        if (config.getItems && active.length === 0) return;
        setBusy(true);
        setError(null);
        let result;
        try {
            result = await config.action.run({ items: active, payload, cardProps, options });
        } catch (err) {
            setBusy(false);
            setError(userErrorMessage(err));
            return;
        }
        setBusy(false);
        if (result?.failuresText) { setError(result.failuresText); return; }
        const count = result?.count ?? (config.getItems ? active.length : undefined);
        setResolvedCount(count ?? null);
        setStatus(config.action.status);
        persist(config.action.status, config.getItems ? count : undefined);
    }, [config, active, payload, cardProps, options, persist]);

    if (!config.isValid(payload)) return null;
    if (config.getItems && !items.length) return null;

    const accent = ACCENTS[config.accent] || ACCENTS.rose;
    const Icon = config.icon;
    const testId = config.testId;

    // A subtle, non-blocking hint that the decision couldn't be saved to the server
    // (the optimistic local state already reflects the choice; it may not survive a
    // reload). Shown in whichever state the card ends up in.
    const SaveHint = () => (saveHint ? (
        <span className="text-[10px] text-amber-600 dark:text-amber-400">{t('cardCantSaveDecision')}</span>
    ) : null);

    // Whether the resolved/cancelled SUMMARY divs carry the card testid. Most cards
    // kept their testid on the summary (book/expense/calendar/ticket/board/… cards);
    // two originally did NOT (document-delete's "Deleted N files" + the contact card's
    // success/declined summaries) — E2E asserts those testids are gone after resolve.
    const summaryTestId = config.summaryHasTestId === false ? undefined : testId;

    // --- Superseded state (#545) ----------------------------------------------
    // A later, corrected card for the same request replaced this one: the server
    // stamped its negative status + `superseded: true`. It reads as replaced, not
    // as a decision the user made.
    const supersededRes = payload?.resolution?.superseded ? payload.resolution : null;
    if (supersededRes && status === supersededRes.status) {
        return (
            <div data-testid="card-superseded" data-card-state="superseded" data-card-key={config.cardKey} className={SUMMARY_CANCELLED}>
                <RefreshCw size={14} className={`${CARD_ICON} mt-px`} />
                <span data-testid="card-summary-text" className={CARD_SUMMARY_TEXT}>
                    {t('cardSuperseded', 'Replaced by the updated card')}
                </span>
            </div>
        );
    }

    // --- Resolved (done) state ------------------------------------------------
    if (status === config.action.status) {
        const count = resolvedCount ?? (config.getItems ? active.length : undefined);
        return (
            <div data-testid={summaryTestId} data-card-state="done" className={config.doneClassName || SUMMARY_DONE}>
                {config.doneIcon ? <config.doneIcon size={14} className={`${CARD_ICON} mt-px`} /> : Icon ? <Icon size={14} className={`${CARD_ICON} mt-px`} /> : null}
                <span data-testid="card-summary-text" className={CARD_SUMMARY_TEXT}>
                    {config.renderDone({ payload, count, active, doneMeta, t })}
                    {saveHint && <> <SaveHint /></>}
                </span>
            </div>
        );
    }
    // --- Cancelled / declined state ------------------------------------------
    if (status === (config.cancel?.status || 'cancelled') || (config.altCancelStatus && status === config.altCancelStatus)) {
        return (
            <div data-testid={summaryTestId} data-card-state="cancelled" className={SUMMARY_CANCELLED}>
                <X size={14} className={`${CARD_ICON} mt-px`} />
                <span data-testid="card-summary-text" className={CARD_SUMMARY_TEXT}>
                    {config.renderCancelled({ payload, items, t })}
                    {saveHint && <> <SaveHint /></>}
                </span>
            </div>
        );
    }

    // Imperative resolver for custom bodies (contact card): set the terminal status
    // + persist. `count` optional; `meta` is session-only render data. Custom bodies
    // own their own busy/error UI.
    const resolve = (nextStatus, count, meta) => {
        if (meta) setDoneMeta(meta);
        setStatus(nextStatus);
        persist(nextStatus, config.getItems ? count : undefined);
    };

    // --- Pending (interactive) state -----------------------------------------
    // `cardRef` (#517): the card reference the contact Confirm sends with its POST
    // so the server refuses (and creates nothing for) an already-answered card;
    // `adoptResolution` shows the decision the server kept; `compact` = narrow card.
    const cardRefInfo = { conversationId, messageId, cardKey: config.cardKey, entryId, actionId };
    const ctx = {
        busy, setBusy, error, setError, removeFromQueue, active, resolve, doCancel, t,
        compact, cardRef: cardRefInfo, adoptResolution, ...cardProps,
    };

    return (
        <div ref={cardRef} data-testid={testId} data-card-state="pending" className={`mt-2 border rounded-lg p-3 min-w-0 max-w-full ${accent.containerBusy}`}>
            <div className={`flex items-start gap-2 text-xs font-semibold mb-2 min-w-0 ${accent.title}`}>
                {Icon && <Icon size={14} className={`${CARD_ICON} mt-px`} />}
                <span className="min-w-0 break-words">{config.title({ t, count: active.length, payload })}</span>
            </div>

            {config.customBody
                ? config.customBody({ payload, ctx, t, error, saveHint, accent })
                : (
                    <>
                        {config.renderDetail
                            ? config.renderDetail({ payload, t })
                            : (
                                <div className="space-y-1.5">
                                    {active.map((it) => (
                                        <React.Fragment key={config.itemKey(it)}>
                                            {config.renderRow({ item: it, ctx })}
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}
                        {config.renderOptions && config.renderOptions({ active, payload, options, setOption, busy, t })}
                        {error && <div className="mt-2 text-[11px] text-rose-600 dark:text-rose-400">{error}</div>}
                        {saveHint && (
                            <div className="mt-1 text-[10px] text-amber-600 dark:text-amber-400">
                                {t('cardCantSaveDecision')}
                            </div>
                        )}
                        <div data-testid="card-action-row" className={CARD_ACTION_ROW}>
                            <button
                                type="button"
                                onClick={handleAction}
                                disabled={busy || (config.getItems && active.length === 0)}
                                className={`inline-flex items-center gap-1 px-4 py-1 text-[11px] font-semibold rounded ${CARD_BTN_FIT} ${accent.actionBtn}`}
                            >
                                {config.action.icon && <config.action.icon size={12} className={CARD_ICON} />}
                                <span className={CARD_BTN_LABEL}>
                                    {busy ? config.action.busyLabel({ t }) : config.action.label({ t, count: active.length })}
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={doCancel}
                                disabled={busy}
                                className={`inline-flex items-center gap-1 px-4 py-1 text-[11px] font-semibold text-pai-secondary bg-white/[0.03] border border-pai hover:bg-white/[0.07] rounded disabled:opacity-50 ${CARD_BTN_FIT}`}
                            >
                                <X size={12} className={CARD_ICON} />
                                <span className={CARD_BTN_LABEL}>{config.cancelLabel ? config.cancelLabel({ t }) : t('cardCancel')}</span>
                            </button>
                            {config.footerNote && (
                                <span className="min-w-0 text-[10px] text-pai-meta break-words">{config.footerNote({ t, payload })}</span>
                            )}
                        </div>
                    </>
                )}
        </div>
    );
};

export default ResolutionCard;
export { persistResolution, persistResolutionResult };
